// Browser stand-in for node-7z (the Nexus fork). The renderer constructs
// `new Zip()` and calls `extractFull(archive, dest, opts, progressCb,
// passwordCb)` expecting a Promise resolving to `{ code, errors }`.
//
// There's no 7z binary in a browser, so extraction is done with fflate
// (pure-JS ZIP). The archive bytes are read through the fs core (the picked
// file mount or the OPFS staging tree) and each entry is written under the
// destination path via the fs core (which routes the staging subtree to
// OPFS). Only ZIP is supported; 7z/RAR archives report an error the caller
// surfaces as a broken/unsupported archive.
const fflate = require("fflate");
const path = require("path");

const core = require("./fsa-core.cjs");

function isZip(bytes) {
    // "PK\x03\x04" (local file header), "PK\x05\x06" (empty archive),
    // or "PK\x07\x08" (spanned)
    if (bytes.length < 4 || bytes[0] !== 0x50 || bytes[1] !== 0x4b) {
        return false;
    }
    return (
        (bytes[2] === 0x03 && bytes[3] === 0x04) ||
        (bytes[2] === 0x05 && bytes[3] === 0x06) ||
        (bytes[2] === 0x07 && bytes[3] === 0x08)
    );
}

async function extractFull(archive, dest, options, progress, _passwordCB) {
    // node-7z allows omitting options: (archive, dest, progress, passwordCB)
    if (typeof options === "function") {
        progress = options;
    }

    let bytes;
    try {
        const data = await core.readFile(archive);
        bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
    } catch (err) {
        return { code: 2, errors: [`cannot read archive: ${err.message}`] };
    }

    if (!isZip(bytes)) {
        return {
            code: 2,
            errors: [
                "Only ZIP archives are supported in the web build " +
                    "(7z/RAR need a native extractor).",
            ],
        };
    }

    let entries;
    try {
        entries = fflate.unzipSync(bytes);
    } catch (err) {
        return { code: 2, errors: [`extraction failed: ${err.message}`] };
    }

    const names = Object.keys(entries);
    const errors = [];
    let done = 0;
    for (const name of names) {
        const content = entries[name];
        // fflate represents directories as zero-length entries ending in "/"
        if (name.endsWith("/")) {
            await core.mkdir(path.join(dest, name)).catch(() => undefined);
            continue;
        }
        try {
            await core.writeFile(path.join(dest, name), content);
        } catch (err) {
            errors.push(`${name}: ${err.message}`);
        }
        done++;
        if (typeof progress === "function") {
            // node-7z emits per-file progress; mimic loosely
            try {
                progress({ percent: Math.round((done / names.length) * 100) });
            } catch (err) {
                /* progress callbacks must never break extraction */
            }
        }
    }

    return { code: errors.length > 0 ? 2 : 0, errors };
}

function unsupported(method) {
    return () =>
        Promise.reject(
            new Error(`node-7z.${method} is not supported in the web build`),
        );
}

class Zip {
    extractFull(archive, dest, options, progress, passwordCB) {
        return extractFull(archive, dest, options, progress, passwordCB);
    }
    // extractFull covers mod installation; the rest are used by flows that
    // are themselves fused off (download import, feedback bundles, ...)
    extract = unsupported("extract");
    add = unsupported("add");
    delete = unsupported("delete");
    update = unsupported("update");
    test = unsupported("test");
    list = unsupported("list");
}

module.exports = Zip;
module.exports.default = Zip;
module.exports.Zip = Zip;
